import React, { useState, useMemo } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    InputAdornment,
    Chip,
    Card,
    CardContent,
    Grid,
    Divider,
    Alert,
    CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import PersonIcon from '@mui/icons-material/Person';

const API_BASE = process.env.REACT_APP_API_BASE;

// Format YYYY-MM-DD → DD/MM/YYYY for display
function fmtDisplay(dateStr) {
    if (!dateStr) return '';
    const [y, m, d] = String(dateStr).split('-');
    return y && m && d ? `${d}/${m}/${y}` : dateStr;
}

// Column definitions for the main table
const COLUMNS = [
    { id: 'sr', label: 'SR.NO', numeric: false, sortable: false },
    { id: 'name', label: 'Worker\'s Name', numeric: false, sortable: true },
    { id: 'payment_date', label: 'Date', numeric: false, sortable: true },
    { id: 'type', label: 'Type', numeric: false, sortable: true },
    { id: 'cash_amount', label: 'Cash (₹)', numeric: true, sortable: true },
    { id: 'online_amount', label: 'Online (₹)', numeric: true, sortable: true },
    { id: 'amount', label: 'Total (₹)', numeric: true, sortable: true },
    { id: 'form_owner', label: 'Form Owner', numeric: false, sortable: true },
];

export default function PeriodicalReport() {
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [data, setData] = useState(null); // { transactions, summary }

    // Sort state
    const [orderBy, setOrderBy] = useState('payment_date');
    const [order, setOrder] = useState('asc');

    // Per-column filter state
    const [filters, setFilters] = useState({
        name: '', payment_date: '', type: '', cash_amount: '', online_amount: '', amount: '', form_owner: '',
    });

    async function handleReport() {
        if (!fromDate || !toDate) {
            setError('Please select both From and To dates.');
            return;
        }
        if (fromDate > toDate) {
            setError('From date cannot be after To date.');
            return;
        }
        setError('');
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/periodical-report?from=${fromDate}&to=${toDate}`, {
                headers: { Accept: 'application/json' },
            });
            if (!res.ok) throw new Error(`Server error ${res.status}`);
            const json = await res.json();
            setData(json);
            // reset filters/sort on fresh load
            setFilters({ name: '', payment_date: '', type: '', cash_amount: '', online_amount: '', amount: '', form_owner: '' });
            setOrderBy('payment_date');
            setOrder('asc');
        } catch (err) {
            setError('Failed to load report: ' + err.message);
        } finally {
            setLoading(false);
        }
    }

    function handleSort(colId) {
        if (orderBy === colId) {
            setOrder(order === 'asc' ? 'desc' : 'asc');
        } else {
            setOrderBy(colId);
            setOrder('asc');
        }
    }

    function handleFilterChange(colId, value) {
        setFilters(f => ({ ...f, [colId]: value }));
    }

    // Filtered + sorted rows
    const processedRows = useMemo(() => {
        if (!data) return [];
        const m = v => (v == null ? '' : String(v).toLowerCase());
        let rows = data.transactions.filter(r => {
            if (filters.name && !m(r.name).includes(m(filters.name))) return false;
            if (filters.payment_date && !m(fmtDisplay(r.payment_date)).includes(m(filters.payment_date))) return false;
            if (filters.type && !m(r.type).includes(m(filters.type))) return false;
            if (filters.cash_amount && !m(r.cash_amount).includes(m(filters.cash_amount))) return false;
            if (filters.online_amount && !m(r.online_amount).includes(m(filters.online_amount))) return false;
            if (filters.amount && !m(r.amount).includes(m(filters.amount))) return false;
            if (filters.form_owner && !m(r.form_owner).includes(m(filters.form_owner))) return false;
            return true;
        });
        // sort
        rows = [...rows].sort((a, b) => {
            let av = a[orderBy] ?? '', bv = b[orderBy] ?? '';
            if (orderBy === 'amount') { av = parseFloat(av) || 0; bv = parseFloat(bv) || 0; }
            let cmp = av < bv ? -1 : av > bv ? 1 : 0;
            return order === 'asc' ? cmp : -cmp;
        });
        return rows;
    }, [data, filters, orderBy, order]);

    const grandTotal = useMemo(() =>
        processedRows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0),
        [processedRows]
    );

    // Summary re-computed based on filtered rows
    const filteredSummary = useMemo(() => {
        const map = {};
        processedRows.forEach(r => {
            const owner = r.form_owner || 'Unknown';
            if (!map[owner]) map[owner] = { form_owner: owner, cash: 0, online: 0, total: 0 };
            const amt = parseFloat(r.amount) || 0;
            if ((r.payment_mode || '').includes('CASH')) map[owner].cash += amt;
            else map[owner].online += amt;
            map[owner].total += amt;
        });
        return Object.values(map).sort((a, b) => a.form_owner.localeCompare(b.form_owner));
    }, [processedRows]);

    const summaryTotals = useMemo(() => ({
        cash: filteredSummary.reduce((s, r) => s + r.cash, 0),
        online: filteredSummary.reduce((s, r) => s + r.online, 0),
        total: filteredSummary.reduce((s, r) => s + r.total, 0),
    }), [filteredSummary]);

    const headerSx = { bgcolor: '#0b4a6f', color: 'white', fontWeight: 'bold', py: 1.5, whiteSpace: 'nowrap' };
    const filterRowSx = { bgcolor: '#e1f5fe', py: 0.75 };

    return (
        <Box sx={{ p: 2 }}>
            {/* ── Header Card ── */}
            <Paper elevation={4} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #0b4a6f 0%, #1976d2 100%)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <AssessmentIcon sx={{ color: 'white', fontSize: 36 }} />
                    <Box>
                        <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>
                            Periodical Transaction Report
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                            Consolidated payment report across all form types
                        </Typography>
                    </Box>
                </Box>

                {/* Date Range + Report Button */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <TextField
                        label="From Date"
                        type="date"
                        value={fromDate}
                        onChange={e => setFromDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        sx={{ bgcolor: 'white', borderRadius: 1, minWidth: 170 }}
                    />
                    <TextField
                        label="To Date"
                        type="date"
                        value={toDate}
                        onChange={e => setToDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        sx={{ bgcolor: 'white', borderRadius: 1, minWidth: 170 }}
                    />
                    <Button
                        variant="contained"
                        onClick={handleReport}
                        disabled={loading}
                        sx={{ bgcolor: '#E65100', '&:hover': { bgcolor: '#BF360C' }, fontWeight: 700, px: 4, height: 40 }}
                        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AssessmentIcon />}
                    >
                        {loading ? 'Loading...' : 'Generate Report'}
                    </Button>

                    {data && (
                        <Chip
                            label={`${processedRows.length} transactions`}
                            sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 600 }}
                        />
                    )}
                </Box>

                {fromDate && toDate && (
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mt: 1.5, fontStyle: 'italic' }}>
                        Period: {fmtDisplay(fromDate)} to {fmtDisplay(toDate)}
                    </Typography>
                )}
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {data && (
                <>
                    {/* ── Main Transaction Table ── */}
                    <Paper elevation={3} sx={{ mb: 3 }}>
                        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0b4a6f' }}>
                                Transaction Details
                            </Typography>
                            <Chip label={`Total: ₹${grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 0 })}`}
                                color="primary" variant="filled" sx={{ fontWeight: 700, fontSize: '0.9rem' }} />
                        </Box>
                        <Divider />
                        <TableContainer sx={{ maxHeight: 'calc(100vh - 380px)' }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    {/* Column headers with sort */}
                                    <TableRow>
                                        {COLUMNS.map(col => (
                                            <TableCell key={col.id} sx={headerSx} align={col.numeric ? 'right' : 'left'}>
                                                {col.sortable ? (
                                                    <TableSortLabel
                                                        active={orderBy === col.id}
                                                        direction={orderBy === col.id ? order : 'asc'}
                                                        onClick={() => handleSort(col.id)}
                                                        sx={{ color: 'white !important', '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                                                    >
                                                        {col.label}
                                                    </TableSortLabel>
                                                ) : col.label}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                    {/* Filter row */}
                                    <TableRow>
                                        <TableCell sx={filterRowSx} />
                                        {['name', 'payment_date', 'type', 'cash_amount', 'online_amount', 'amount', 'form_owner'].map(col => (
                                            <TableCell key={col} sx={filterRowSx}>
                                                <TextField
                                                    size="small"
                                                    variant="standard"
                                                    value={filters[col]}
                                                    onChange={e => handleFilterChange(col, e.target.value)}
                                                    placeholder="Filter..."
                                                    InputProps={{
                                                        disableUnderline: true,
                                                        startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="disabled" /></InputAdornment>,
                                                    }}
                                                    sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid #bbb', minWidth: 80 }}
                                                />
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {processedRows.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center" sx={{ py: 4, color: '#888' }}>
                                                No transactions found for the selected date range / filters.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        <>
                                            {processedRows.map((r, idx) => (
                                                <TableRow key={idx} hover sx={{ '&:nth-of-type(even)': { bgcolor: '#f9fbff' } }}>
                                                    <TableCell sx={{ color: '#666', fontWeight: 600 }}>{idx + 1}</TableCell>
                                                    <TableCell sx={{ fontWeight: 500 }}>{r.name}</TableCell>
                                                    <TableCell>{fmtDisplay(r.payment_date)}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={r.type}
                                                            size="small"
                                                            variant="outlined"
                                                            color={
                                                                r.type === 'NEW FORM' ? 'primary'
                                                                    : r.type === 'RENEWAL' ? 'success'
                                                                        : r.type === 'NOMINEE' ? 'secondary'
                                                                            : r.type.startsWith('APP') ? 'warning'
                                                                                : 'default'
                                                            }
                                                            sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                                                        {parseFloat(r.cash_amount || 0) > 0 ? `₹${parseFloat(r.cash_amount).toLocaleString('en-IN')}` : '—'}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600, color: '#1565c0' }}>
                                                        {parseFloat(r.online_amount || 0) > 0 ? `₹${parseFloat(r.online_amount).toLocaleString('en-IN')}` : '—'}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                        ₹{parseFloat(r.amount || 0).toLocaleString('en-IN')}
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 600, color: '#0b4a6f' }}>{r.form_owner}</TableCell>
                                                </TableRow>
                                            ))}
                                            {/* Grand Total row */}
                                            <TableRow sx={{ bgcolor: '#e8f5e9', '& td': { fontWeight: 700, fontSize: '0.95rem' } }}>
                                                <TableCell colSpan={4} align="right" sx={{ color: '#2e7d32' }}>TOTAL</TableCell>
                                                <TableCell align="right" sx={{ color: '#2e7d32' }}>
                                                    ₹{processedRows.reduce((s,r)=>s+(parseFloat(r.cash_amount)||0),0).toLocaleString('en-IN')}
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: '#1565c0' }}>
                                                    ₹{processedRows.reduce((s,r)=>s+(parseFloat(r.online_amount)||0),0).toLocaleString('en-IN')}
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: '#E65100' }}>
                                                    ₹{grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 0 })}
                                                </TableCell>
                                                <TableCell />
                                            </TableRow>
                                        </>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>

                    {/* ── Summary Section ── */}
                    {filteredSummary.length > 0 && (
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0b4a6f', mb: 2 }}>
                                Summary by Form Owner
                            </Typography>
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                {filteredSummary.map(owner => (
                                    <Grid item xs={12} sm={6} md={4} lg={3} key={owner.form_owner}>
                                        <Card elevation={3} sx={{
                                            border: '1px solid #e0e0e0',
                                            borderRadius: 2,
                                            overflow: 'hidden',
                                            transition: 'box-shadow 0.2s',
                                            '&:hover': { boxShadow: 6 }
                                        }}>
                                            {/* Card Header */}
                                            <Box sx={{ bgcolor: '#0b4a6f', px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <PersonIcon sx={{ color: 'white', fontSize: 20 }} />
                                                <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 700, flexGrow: 1 }}>
                                                    {owner.form_owner || 'Unknown'}
                                                </Typography>
                                                <Chip
                                                    label={`₹${owner.total.toLocaleString('en-IN')}`}
                                                    size="small"
                                                    sx={{ bgcolor: '#E65100', color: 'white', fontWeight: 700 }}
                                                />
                                            </Box>
                                            <CardContent sx={{ p: 2 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                    <Box sx={{ textAlign: 'center', flex: 1 }}>
                                                        <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>Cash</Typography>
                                                        <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 700 }}>
                                                            ₹{owner.cash.toLocaleString('en-IN')}
                                                        </Typography>
                                                    </Box>
                                                    <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                                                    <Box sx={{ textAlign: 'center', flex: 1 }}>
                                                        <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>Online</Typography>
                                                        <Typography variant="h6" sx={{ color: '#1565c0', fontWeight: 700 }}>
                                                            ₹{owner.online.toLocaleString('en-IN')}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Divider sx={{ my: 1 }} />
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <CurrencyRupeeIcon fontSize="small" sx={{ color: '#E65100' }} />
                                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>Total</Typography>
                                                    </Box>
                                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#E65100' }}>
                                                        ₹{owner.total.toLocaleString('en-IN')}
                                                    </Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>

                            {/* Summary Grand Total Card */}
                            <Card elevation={4} sx={{
                                background: 'linear-gradient(135deg, #E65100 0%, #FF6D00 100%)',
                                borderRadius: 2,
                            }}>
                                <CardContent sx={{ p: 2 }}>
                                    <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 700, mb: 1.5 }}>
                                        Grand Total ({filteredSummary.length} Form Owners)
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', display: 'block' }}>Total Cash</Typography>
                                            <Typography variant="h5" sx={{ color: 'white', fontWeight: 800 }}>
                                                ₹{summaryTotals.cash.toLocaleString('en-IN')}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', display: 'block' }}>Total Online</Typography>
                                            <Typography variant="h5" sx={{ color: 'white', fontWeight: 800 }}>
                                                ₹{summaryTotals.online.toLocaleString('en-IN')}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', display: 'block' }}>Grand Total</Typography>
                                            <Typography variant="h4" sx={{ color: 'white', fontWeight: 900 }}>
                                                ₹{summaryTotals.total.toLocaleString('en-IN')}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
}
